import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { aiService, PlannerAIAction } from "_services/ia.service";
import { useTranslation } from "react-i18next";

type ModalPlannerAIProps = {
  idVoyage: string | number;
  isOpen: boolean;
  nbJours: number;
  jours?: {
    num_jour: number;
    date_journee: string;
  }[];
  onClose: () => void;
  onApplyActions?: (actions: any[]) => Promise<void> | void;
};

const actionLabelKeys: Record<PlannerAIAction, string> = {
  ADD_ACTIVITIES: "modalPlannerAI.addActivities",
  FIX_TRANSPORTS: "modalPlannerAI.fixTransports",
  REBALANCE_NIGHTS: "modalPlannerAI.rebalanceNights",
  CHECK_ITINERARY: "modalPlannerAI.checkItinerary",
  ASK_QUESTION: "modalPlannerAI.askQuestion",
};

const quickActions: PlannerAIAction[] = [
  "ADD_ACTIVITIES",
  "FIX_TRANSPORTS",
  "REBALANCE_NIGHTS",
  "CHECK_ITINERARY",
];

const getActionTypeKey = (actionOrType?: any) => {
  const type =
    typeof actionOrType === "string"
      ? actionOrType
      : actionOrType?.type;

  const typeRepas =
    typeof actionOrType === "string"
      ? ""
      : actionOrType?.typeRepas;

  if (type === "ADD_ACTIVITY" && typeRepas === "O") {
    return "modalPlannerAI.meal";
  }

  switch (type) {
    case "ADD_ACTIVITY":
      return "modalPlannerAI.place";

    case "ADD_TRANSPORT":
      return "modalPlannerAI.transport";

    case "UPDATE_TRANSPORT":
      return "modalPlannerAI.transportToFix";

    case "SUGGEST_REBALANCE":
      return "modalPlannerAI.rebalance";

    case "SUGGEST_CHANGE":
      return "modalPlannerAI.suggestion";

    case "INFO":
      return "modalPlannerAI.information";

    default:
      return "modalPlannerAI.proposal";
  }
};

type AssistantRobotVisualProps = {
  size?: "sm" | "md" | "lg";
  className?: string;
};

const AssistantRobotVisual: React.FC<AssistantRobotVisualProps> = ({
  size = "md",
  className = "",
}) => {
  const sizes = {
    sm: {
      wrapper: "h-10 w-10",
      head: "h-7 w-8",
      eye: "h-1.5 w-1.5",
      antenna: "h-2.5",
      ear: "h-3 w-1.5",
      glow: "h-12 w-12",
    },
    md: {
      wrapper: "h-14 w-14",
      head: "h-9 w-11",
      eye: "h-2 w-2",
      antenna: "h-3",
      ear: "h-4 w-2",
      glow: "h-16 w-16",
    },
    lg: {
      wrapper: "h-24 w-24",
      head: "h-14 w-[72px]",
      eye: "h-3 w-3",
      antenna: "h-5",
      ear: "h-6 w-3",
      glow: "h-28 w-28",
    },
  };

  const s = sizes[size];

  return (
    <div
      className={`
        relative
        flex
        ${s.wrapper}
        items-center
        justify-center
        ${className}
      `}
    >
      <span
        className={`
          absolute
          ${s.glow}
          rounded-full
          bg-violet-500/20
          blur-xl
          dark:bg-[#FF4A52]/18
        `}
      />

      <span className="absolute -top-1 right-1 h-1.5 w-1.5 rounded-full bg-violet-400 shadow-[0_0_12px_rgba(139,92,246,0.75)] dark:bg-[#FF4A52]" />
      <span className="absolute left-1 top-2 text-[10px] text-[#FF4A52]">✦</span>
      <span className="absolute right-0 top-4 text-[9px] text-violet-400">✧</span>

      <div className="relative flex flex-col items-center">
        <span
          className={`
            ${s.antenna}
            w-0.5
            rounded-full
            bg-gradient-to-b
            from-violet-500
            to-violet-300
            dark:from-[#FF4A52]
            dark:to-[#FF4A52]/45
          `}
        />

        <div className="relative flex items-center justify-center">
          <span
            className={`
              absolute
              -left-1.5
              ${s.ear}
              rounded-full
              bg-white
              shadow-[inset_0_0_0_1px_rgba(124,58,237,0.14)]
              dark:bg-slate-900
              dark:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]
            `}
          />

          <div
            className={`
              relative
              ${s.head}
              rounded-[999px]
              bg-white
              shadow-[0_12px_30px_rgba(124,58,237,0.25),inset_0_0_0_1px_rgba(124,58,237,0.12)]
              dark:bg-slate-950
              dark:shadow-[0_12px_30px_rgba(0,0,0,0.35),inset_0_0_0_1px_rgba(255,255,255,0.08)]
            `}
          >
            <div
              className="
                absolute
                inset-x-1.5
                top-1/2
                flex
                -translate-y-1/2
                items-center
                justify-center
                gap-1.5
                rounded-full
                bg-gradient-to-r
                from-violet-700
                via-purple-600
                to-violet-500
                py-1.5
                shadow-[0_0_18px_rgba(124,58,237,0.45)]
                dark:from-[#4c1d95]
                dark:via-[#7c3aed]
                dark:to-[#FF4A52]
              "
            >
              <span className={`${s.eye} rounded-full bg-white shadow-[0_0_8px_rgba(255,255,255,0.9)]`} />
              <span className={`${s.eye} rounded-full bg-white shadow-[0_0_8px_rgba(255,255,255,0.9)]`} />
            </div>
          </div>

          <span
            className={`
              absolute
              -right-1.5
              ${s.ear}
              rounded-full
              bg-white
              shadow-[inset_0_0_0_1px_rgba(124,58,237,0.14)]
              dark:bg-slate-900
              dark:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]
            `}
          />
        </div>
      </div>
    </div>
  );
};

const ModalPlannerAI: React.FC<ModalPlannerAIProps> = ({
  idVoyage,
  isOpen,
  nbJours,
  jours = [],
  onClose,
  onApplyActions,
}) => {
  const { t } = useTranslation();
const [selectedAction, setSelectedAction] =
  useState<PlannerAIAction | null>(null);

  const [userMessage, setUserMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showResultApproaching, setShowResultApproaching] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  const [aiResponse, setAiResponse] = useState<any | null>(null);
  const [error, setError] = useState("");

  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [keptActions, setKeptActions] = useState<any[]>([]);
  const [conversationHistory, setConversationHistory] = useState<PlannerAIMessage[]>([]);
const [lastAction, setLastAction] = useState<PlannerAIAction | null>(null);

const getUserMessagePlaceholder = () => {
  /*
   * Tant que l'IA n'a pas encore répondu,
   * on conserve le placeholder général.
   */
  if (!aiResponse || !lastAction) {
    return t("modalPlannerAI.placeholder");
  }

  switch (lastAction) {
    case "ADD_ACTIVITIES":
      return t("modalPlannerAI.placeholderAfterActivities");

    case "FIX_TRANSPORTS":
      return t("modalPlannerAI.placeholderAfterTransports");

    case "REBALANCE_NIGHTS":
      return t("modalPlannerAI.placeholderAfterNights");

    case "CHECK_ITINERARY":
      return t("modalPlannerAI.placeholderAfterCheck");

    case "ASK_QUESTION":
      return t("modalPlannerAI.placeholderAfterQuestion");

    default:
      return t("modalPlannerAI.placeholder");
  }
};

useEffect(() => {
  if (!isLoading) {
    setShowResultApproaching(false);
    return;
  }

  const timer = setTimeout(() => {
    setShowResultApproaching(true);
  }, 4000);

  return () => {
    clearTimeout(timer);
  };
}, [isLoading]);

const closeModal = () => {
  if (isApplying) return;

  setError("");
  setAiResponse(null);
  setConversationHistory([]);
  setLastAction(null);
  setSelectedAction(null);
  setUserMessage("");
  onClose();
};

type PlannerAIMessage = {
  role: "user" | "assistant";
  content: string;
};



const isDisplayableAction = (action: any) => {
  if (!action) return false;

  if (action.type === "INFO") {
    return Boolean(
      action.title ||
      action.reason ||
      action.description ||
      action.from ||
      action.to ||
      action.suggestedNights?.length
    );
  }

  return true;
};

const displayableActions = useMemo(() => {
  return keptActions.filter(isDisplayableAction);
}, [keptActions]);

const isApplicableAction = (action: any) => {
  return [
    "ADD_ACTIVITY",
    "ADD_TRANSPORT",
    "UPDATE_TRANSPORT",
    "SUGGEST_REBALANCE",
  ].includes(action?.type);
};

const applicableActions = useMemo(() => {
  return keptActions.filter(isApplicableAction);
}, [keptActions]);

const canApplyActions = useMemo(() => {
  return applicableActions.length > 0;
}, [applicableActions]);

const removeKeptAction = (indexToRemove: number) => {
  setKeptActions((prev) =>
    prev.filter((_, index) => index !== indexToRemove)
  );
};

const handleSendToAI = () => {
  const trimmedMessage = userMessage.trim();

  if (!selectedAction && !trimmedMessage) {
    setError("Choisis une action ou écris une demande avant d’envoyer à l’IA.");
    return;
  }

  const actionToSend: PlannerAIAction =
    selectedAction || "ASK_QUESTION";

  callAssistant(actionToSend);
};

const callAssistant = async (action: PlannerAIAction) => {
  try {
    setIsLoading(true);
    setAiResponse(null);
    setKeptActions([]);
    setError("");
    const currentUserMessage = userMessage.trim();

    const historyToSend =
      lastAction === action ? conversationHistory : [];

    const response = await aiService.assistantPlanificateur(
      action,
      idVoyage,
      currentUserMessage,
      selectedDay,
      historyToSend
    );

    const aiData = response.data;

    console.log("aidate",aiData);

    setAiResponse(response.data);
    setKeptActions(response.data?.actions || []);


    setConversationHistory((prev) => [
      ...(lastAction === action ? prev : []),
      ...(currentUserMessage
        ? [{ role: "user" as const, content: currentUserMessage }]
        : []),
      {
        role: "assistant" as const,
        content:
          aiData.question ||
          aiData.message ||
          "L’assistant a répondu.",
      },
    ]);

    setLastAction(action);
    setUserMessage("");
  } catch (err: any) {
    console.error(err);

    const status = err?.response?.status;
    const apiError = err?.response?.data?.error;

    if (status === 428) {
      setError(
        apiError ||
          "Tu dois être connecté(e) à un compte pour continuer à utiliser l’assistant IA."
      );
    } else if (status === 429) {
      setError(
        apiError ||
          "La limite de requêtes IA est atteinte. Réessaie un peu plus tard."
      );
    } else if (status === 400) {
      setError(apiError || "La demande envoyée à l’assistant est incomplète.");
    } else {
      setError(apiError || "Erreur lors de l’appel à l’assistant IA.");
    }
  } finally {
    setIsLoading(false);
  }
};

const getTransportTypeLabel = (type?: string) => {
  switch (type) {
    case "plane":
      return "Avion";
    case "train":
      return "Train/métro";
    case "car":
      return "Voiture";
    case "bus":
      return "Bus";
    case "taxi":
      return "Taxi";
    case "ship":
      return "Bateau";
    case "walking":
      return "À pied";
    case "bicycle":
      return "Vélo";
    case "motorcycle":
      return "Moto/Scooter";
    default:
      return type || "";
  }
};

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[9999]"
        onClose={closeModal}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
           <div className="fixed inset-0 bg-neutral-950/55 backdrop-blur-[2px]" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-3 sm:items-center sm:p-6">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-8 scale-95"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 scale-100"
              leaveTo="opacity-0 translate-y-8 scale-95"
            >
              <Dialog.Panel
                className="
                  w-full
                  max-w-3xl
                  overflow-hidden
                  rounded-[28px]
                  border
                  border-neutral-200
                  bg-white
                  shadow-2xl
                dark:shadow-[0_28px_90px_rgba(0,0,0,0.50),inset_0_1px_0_rgba(255,255,255,0.06)]
                  dark:bg-[linear-gradient(145deg,rgba(15,23,42,0.98)_0%,rgba(17,24,39,0.96)_55%,rgba(10,15,28,0.98)_100%)]

                  dark:border-neutral-800
                "
              >
                {/* Header */}
               <div
  className="
    flex
    items-start
    justify-between
    gap-4

    border-b
    border-neutral-200
    dark:border-white/10

    bg-[radial-gradient(circle_at_0%_0%,rgba(255,74,82,0.10)_0%,transparent_34%),linear-gradient(145deg,rgba(255,255,255,0.92)_0%,rgba(255,255,255,0.78)_100%)]
    dark:bg-[radial-gradient(circle_at_0%_0%,rgba(255,74,82,0.11)_0%,transparent_36%),linear-gradient(145deg,rgba(15,23,42,0.96)_0%,rgba(10,15,28,0.98)_100%)]

    px-5
    py-4
  "
>
                  <div>
                    <Dialog.Title className="flex items-center gap-2 text-lg font-black text-neutral-950 dark:text-white">
                      <AssistantRobotVisual size="sm" className="shrink-0" />
                      {t("modalPlannerAI.title")}  <div className="relative -top-3 ml-0 inline-flex group">
  <button
  type="button"
  tabIndex={-1}
  aria-label={t("modalPlannerAI.betaLabel")}
  className="
    inline-flex
    items-center
    rounded-full
    bg-violet-50
    px-1
    py-0
    text-[8px]
    font-semibold
    uppercase
    tracking-[0.1em]
    text-violet-600
    ring-1
    ring-violet-200
    cursor-help
    transition
    hover:bg-violet-100
    dark:bg-violet-900/30
    dark:text-violet-200
    dark:ring-violet-700
  "
>
  BETA
</button>

  <div
    role="tooltip"
    className="
  pointer-events-none
  absolute
  left-1/2
  top-full
  z-[9999]
  mt-2
  w-64
  -translate-x-1/2
  rounded-xl
  bg-[#0f172a]
  px-3
  py-2
  text-[11px]
  font-medium
  leading-snug
  text-white
  opacity-0
  shadow-2xl
  ring-1
  ring-white/10
  transition-opacity
  duration-200
  group-hover:opacity-100
"
  >
    {t("modalPlannerAI.betaNotice")}
  </div>
</div>
                    </Dialog.Title>
                  </div>

                  <button
                    type="button"
                    onClick={closeModal}
                    className="
                      flex
                      h-9
                      w-9
                      items-center
                      justify-center
                      rounded-full
                      bg-white/80
                      text-neutral-500
                      shadow-sm
                      hover:bg-white
                      dark:bg-neutral-900
                      dark:text-neutral-300
                    "
                  >
                    <i className="las la-times text-lg" />
                  </button>
                </div>

                {/* Body */}
                <div className="max-h-[75vh] overflow-y-auto px-5 py-5 dark:bg-transparent">
                    <div className="mb-5">
  <label className="mb-2 block text-sm font-black text-neutral-950 dark:text-white">
    {t("modalPlannerAI.concernedDay")}
  </label>

  <select
    value={selectedDay ?? ""}
    onChange={(e) => {
      const value = e.target.value;
      setSelectedDay(value ? Number(value) : null);
    }}
    className="
      w-full
      rounded-2xl
      border
      border-neutral-200
      bg-white
      px-4
      py-3
      text-sm
      font-semibold
      text-neutral-900
      outline-none
      focus:border-violet-400
      focus:ring-2
      focus:ring-violet-100
      dark:border-neutral-800
      dark:bg-slate-950/45
      dark:text-white
      dark:focus:ring-[rgba(255,74,82,0.18)]
    "
  >
    <option value="">{t("modalPlannerAI.allDays")}</option>

    {jours.length > 0
  ? jours.map((jour) => (
      <option key={jour.num_jour} value={jour.num_jour}>
        {t("jour")} {jour.num_jour} - {jour.date_journee}
      </option>
    ))
  : Array.from({ length: nbJours }, (_, index) => {
      const day = index + 1;

      return (
        <option key={day} value={day}>
          {t("jour")} {day}
        </option>
      );
    })}
  </select>

</div>
                  {/* Actions rapides */}
                  <div>
                    <p className="mb-3 text-sm font-black text-neutral-950 dark:text-white">
                      {t("modalPlannerAI.quickActions")}
                    </p>

                    <div className="grid gap-2 sm:grid-cols-2">
                      {quickActions.map((action) => (
                        <button
                          key={action}
                          type="button"
                          disabled={isLoading}
                            onClick={() => {
                            setSelectedAction((prevAction: any) => {
                                const nextAction = prevAction === action ? null : action;

                                if (nextAction !== prevAction) {
                                setConversationHistory([]);
                                setLastAction(nextAction);
                                }

                                return nextAction;
                            });

                            setAiResponse(null);
                            setError("");
                            }}
                          className={`
                            flex
                            items-center
                            justify-between
                            gap-3
                            rounded-2xl
                            border
                            px-4
                            py-3
                            text-left
                            text-sm
                            font-bold
                            transition
                            hover:-translate-y-0.5
                            disabled:cursor-not-allowed
                            disabled:opacity-60
                            ${
                              selectedAction === action
                                ? "border-violet-300 bg-violet-50 text-violet-700 dark:border-violet-800 dark:bg-violet-950/30 dark:text-violet-300"
                                : "border-neutral-200 bg-white text-neutral-800 hover:shadow-md dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100"
                            }
                          `}
                        >
                         <span>{t(actionLabelKeys[action])}</span>
                          <i className="las la-angle-right text-base opacity-60" />
                        </button>
                      ))}
                    </div>

                  </div>

                  {/* Question libre */}
                  <div className="mt-5">
                    <label className="mb-2 block text-sm font-black text-neutral-950 dark:text-white">
                     {t("modalPlannerAI.customRequest")}
                    </label>

                    <textarea
                      value={userMessage}
                      onChange={(e) => setUserMessage(e.target.value)}
                      placeholder={getUserMessagePlaceholder()}
                      className="
                        min-h-[80px]
                        w-full
                        resize-none
                        rounded-2xl
                        border
                        border-neutral-200
                        bg-white
                        px-4
                        py-3
                        text-sm
                        text-neutral-900
                        outline-none
                        focus:border-violet-400
                        focus:ring-2
                        focus:ring-violet-100
                        dark:border-neutral-800
                        dark:bg-slate-950/45
                        dark:text-white
                        dark:focus:ring-[rgba(255,74,82,0.18)]
                      "
                    />

                             <div className="mt-5 flex justify-end">
                                <button
  type="button"
  onClick={() => {
    setConversationHistory([]);
    setLastAction(null);
    setAiResponse(null);
    setError("");
  }}
  className="text-xs mr-10 font-bold text-neutral-500 underline hover:text-neutral-900 dark:hover:text-white"
>
  {t("modalPlannerAI.forgetConversation")}
</button>
  <button
    type="button"
    disabled={isLoading}
    onClick={handleSendToAI}
    className="
      inline-flex
      items-center
      justify-center
      gap-2
      rounded-2xl
      bg-gradient-to-r
      from-violet-600
      to-purple-600
      px-5
      py-2.5
      text-sm
      font-bold
      text-white
      shadow-lg
      shadow-violet-600/25
      transition
      hover:-translate-y-0.5
      disabled:cursor-not-allowed
      disabled:opacity-50
    "
  >
    {isLoading ? (
      <>
        <i className="las la-spinner la-spin" />
        {t("modalPlannerAI.analysisInProgress")}
      </>
    ) : (
      <>
        ✨ {t("modalPlannerAI.sendToAI")}
      </>
    )}
  </button>
</div>

                  </div>



                  {isLoading && (
  <div
    className="
      mt-5
      flex
      items-center
      gap-3
      rounded-2xl
      border
      border-violet-100
      bg-violet-50
      px-4
      py-4
      text-sm
      font-semibold
      text-violet-700

      dark:border-[#FF4A52]/35
      dark:bg-[#FF4A52]/12
      dark:text-[#FF4A52]
    "
  >
    <AssistantRobotVisual size="sm" className="animate-pulse" />

    <div>
  <p className="font-black">
    {showResultApproaching
      ? t("resultatApproche")
      : t("modalPlannerAI.analysisInProgress")}
  </p>

  <p className="mt-0.5 text-xs font-semibold opacity-75">
    {showResultApproaching
      ? t("veuillezPatienter")
      : t("modalPlannerAI.assistantAnalyzing")}
  </p>
</div>
  </div>
)}

                  {/* Erreur */}
                  {error && (
                    <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-4 text-sm font-semibold text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
                      {error}
                    </div>
                  )}

                  {/* Réponse IA */}
                  {aiResponse && (
                    <div className="mt-5 space-y-4">
                      <div className="rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-4 dark:border-white/10
  dark:!bg-[linear-gradient(145deg,rgba(15,23,42,0.92)_0%,rgba(10,15,28,0.96)_100%)]

  dark:shadow-[0_12px_28px_rgba(0,0,0,0.28)]">
                        <p className="text-sm font-black text-neutral-950 dark:text-white">
                          {t("modalPlannerAI.assistantResponse")}
                        </p>

                        <p className="mt-2 text-sm leading-relaxed text-neutral-700 dark:text-neutral-300">
                          {aiResponse.message}
                        </p>
                      </div>

                      {aiResponse.summary?.length > 0 && (
                        <div className="rounded-2xl border border-neutral-200 bg-white px-4 py-4 dark:border-white/10
  dark:!bg-[linear-gradient(145deg,rgba(15,23,42,0.92)_0%,rgba(10,15,28,0.96)_100%)]

  dark:shadow-[0_12px_28px_rgba(0,0,0,0.28)]">
                          <p className="mb-2 text-sm font-black text-neutral-950 dark:text-white">
                            {t("modalPlannerAI.summary")}
                          </p>

                          <ul className="space-y-2 text-sm text-neutral-700 dark:text-neutral-300">
                            {aiResponse.summary.map((item: string, index: number) => (
                              <li key={index} className="flex gap-2">
                                <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-violet-500" />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {aiResponse.warnings?.length > 0 && (
                        <div className="rounded-2xl border border-orange-200 bg-orange-50 px-4 py-4  dark:border-orange-400/30
  dark:bg-[linear-gradient(145deg,rgba(67,42,20,0.72)_0%,rgba(35,24,18,0.88)_100%)]
  dark:shadow-[0_10px_26px_rgba(0,0,0,0.28)]">
                          <p className="mb-2 text-sm font-black text-orange-800 dark:text-orange-300">
                            {t("modalPlannerAI.warnings")}
                          </p>

                          <ul className="space-y-2 text-sm text-orange-700 dark:text-orange-200">
                            {aiResponse.warnings.map((item: string, index: number) => (
                              <li key={index}>• {item}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                   {displayableActions.length > 0 && (
                        <div className="rounded-2xl border border-neutral-200 bg-white px-4 py-4 dark:border-white/10
  dark:!bg-[linear-gradient(145deg,rgba(15,23,42,0.92)_0%,rgba(10,15,28,0.96)_100%)]
  dark:shadow-[0_12px_28px_rgba(0,0,0,0.28)]">
                          
                         {displayableActions.every((action: any) => action.type === "INFO") ? (
                        <p className="mb-3 text-sm font-black text-neutral-950 dark:text-white">
                            {t("modalPlannerAI.informations")}
                        </p>
                        ) : (
                        <p className="mb-3 text-sm font-black text-neutral-950 dark:text-white">
                            {t("modalPlannerAI.suggestedActions")}
                        </p>
                        )}

                          {displayableActions.length > 1 && displayableActions[0].activityMode === "single_place" && (
                                    <p className=" mb-2 text-xxs font-bold text-violet-600 dark:text-violet-300">
                                        {t("modalPlannerAI.groupSinglePlaces")}
                                    </p>
                                    )}
                        

                          <div className="space-y-2">
                            {keptActions.map((action: any, index: number) => {
                                if (!isDisplayableAction(action)) return null;

                                return (
                                    <div
                                    key={index}
                                    className="
                                        rounded-xl
                                        border
                                        border-neutral-200
                                        bg-neutral-50
                                        px-3
                                        py-3
                                        text-sm
                                         dark:border-white/10
      dark:!bg-[linear-gradient(145deg,rgba(15,23,42,0.92)_0%,rgba(10,15,28,0.96)_100%)]
                                    "
                                    >
                               <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">

                                    {action.dayNumber && (
                                      <p className="mt-1 text-xs font-bold text-violet-600 dark:text-violet-300">
                                        {t("jour")} {action.dayNumber}
                                      </p>
                                    )}
                                    {!action.reason  && (
                                    <p className="font-black text-neutral-950 dark:text-white">
                                     {action.title || t(getActionTypeKey(action))}
                                    </p>
                                    )
                                    }
                                    

                                    {action.reason && (
                                    <p className="mt-1 whitespace-pre-line text-neutral-600 dark:text-neutral-400">
                                        {action.reason}
                                    </p>
                                    )}

                                     {action.description && (
                                    <p className="mt-1 whitespace-pre-line text-neutral-600 dark:text-neutral-400">
                                        {action.description}
                                    </p>
                                    )}

                                    {action.activityMode === "grouped_day" && (
                                    <p className="mt-1 text-xxs font-bold text-violet-600 dark:text-violet-300">
                                         {t("modalPlannerAI.groupedDayInfo")}
                                    </p>
                                    )}


                                    {action.from && action.to && (
                                      <p className="mt-1 text-xs text-neutral-500">
                                        {action.from} → {action.to}
                                      </p>
                                    )}
                                    {action.transportType && (
                                        <p className="mt-1 text-xs font-bold text-neutral-600 dark:text-neutral-400">
                                            {t("modalPlannerAI.transportMean")} {getTransportTypeLabel(action.transportType)}
                                        </p>
                                        )}
                                        {(action.distance || action.duration) && (
  <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
    {action.distance && `${action.distance} km`}
    {action.distance && action.duration && " · "}
    {action.duration && action.duration}
  </p>
)}

                                    {action.suggestedNights?.length > 0 && (
  <div className="mt-4 space-y-2">
    <p className="text-xs font-black uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
      {t("modalPlannerAI.itineraryProposal")}
    </p>
    <p className=" mb-2 text-xxs font-bold text-violet-600 dark:text-violet-300">
        {t("modalPlannerAI.nightsOnlyModified")}
    </p>

    <div className="overflow-hidden rounded-xl border border-neutral-200 dark:border-neutral-800">
      {action.suggestedNights.map((step: any, stepIndex: number) => (
        <div
          key={stepIndex}
          className="
            border-b
            border-neutral-200
            bg-white
            px-3
            py-3
            last:border-b-0
            dark:border-white/10
      dark:!bg-[linear-gradient(145deg,rgba(15,23,42,0.92)_0%,rgba(10,15,28,0.96)_100%)]
          "
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-black text-neutral-950 dark:text-white">
                {step.city}
                {step.country ? `, ${step.country}` : ""}
              </p>

              <p className="mt-1 text-xs font-bold text-violet-600 dark:text-violet-300">
                {t("modalPlannerAI.days")} {step.startDay} {t("modalPlannerAI.to")} {step.endDay} · {step.nights} {Number(step.nights) > 1 ? t("modalPlannerAI.nights") : t("modalPlannerAI.night")}
              </p>

              {step.reason && (
                <p className="mt-1 text-xs leading-relaxed text-neutral-600 dark:text-neutral-400">
                  {step.reason}
                </p>
              )}
            </div>

            <span className="whitespace-nowrap rounded-full bg-violet-100 px-2.5 py-1 text-[10px] font-black text-violet-700  dark:bg-[#FF4A52]/14
    dark:text-[#FF4A52]
    dark:border
    dark:border-[#FF4A52]/25">
              {t("modalPlannerAI.stage")} {stepIndex + 1}
            </span>
          </div>
        </div>
      ))}
    </div>
  </div>
)}
                                  </div>
 {!action.reason  && (
                                 <div className="flex flex-col items-end gap-2">
  <span className="rounded-full bg-violet-100 px-2.5 py-1 text-[10px] font-black text-violet-700 dark:bg-[#FF4A52]/14
    dark:text-[#FF4A52]
    dark:border
    dark:border-[#FF4A52]/25">
    {t(getActionTypeKey(action))}
  </span>

  <button
    type="button"
    onClick={() => removeKeptAction(index)}
    disabled={isApplying}
    className="
      inline-flex
      items-center
      gap-1
      rounded-full
      border
      border-red-200
      bg-red-50
      px-2.5
      py-1
      text-[10px]
      font-black
      text-red-600
      transition
      hover:bg-red-100
      dark:border-red-900
      dark:bg-red-950/30
      dark:text-red-300
      disabled:cursor-not-allowed
disabled:opacity-50
    "
  >
    <i className="las la-times" />
    {t("modalPlannerAI.remove")}
  </button>
</div> )}
                                </div>
                              </div>
                                );
})}
                          </div>
                        </div>
                      )}

                      {aiResponse.question && (
                        <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-4 text-sm font-semibold text-blue-700 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-300">
                          {aiResponse.question}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Footer */}
       <div className="flex items-center justify-between gap-3 border-t border-neutral-200 px-5 py-4 dark:border-neutral-800">
  <div>
    {isApplying && (
      <div className="flex items-center gap-2 text-xs font-bold text-violet-700 dark:text-violet-300">
        <i className="las la-spinner la-spin text-base" />
        {t("modalPlannerAI.processing")}
      </div>
    )}
  </div>

  <div className="flex items-center gap-3">
    <button
      type="button"
      disabled={isApplying}
      onClick={closeModal}
      className="
        rounded-2xl
        border
        border-neutral-200
        bg-white
        px-4
        py-2.5
        text-sm
        font-bold
        text-neutral-700
        hover:bg-neutral-50

        disabled:cursor-not-allowed
        disabled:opacity-50

        dark:border-white/10
        dark:bg-slate-950
        dark:text-neutral-500
        dark:hover:bg-slate-900
      "
    >
      {t("modalPlannerAI.close")}
    </button>

    {canApplyActions && (
      <button
        type="button"
        disabled={isApplying}
        onClick={async () => {
          if (!applicableActions.length || isApplying) return;

          try {
            setIsApplying(true);
            setError("");

            await onApplyActions?.(applicableActions);

            setIsApplying(false);
            closeModal();
          } catch (error) {
            console.error(error);
            setIsApplying(false);
            setError(t("modalPlannerAI.applyError"));
          }
        }}
        className="
          inline-flex
          items-center
          justify-center
          gap-2
          rounded-2xl
          bg-gradient-to-r
          from-violet-600
          to-purple-600
          px-4
          py-2.5
          text-sm
          font-bold
          text-white
          shadow-lg
          shadow-violet-600/25
          transition

          disabled:cursor-not-allowed
          disabled:opacity-50
        "
      >
        {isApplying ? (
          <>
            <i className="las la-spinner la-spin" />
            {t("modalPlannerAI.processingShort")}
          </>
        ) : (
          t("modalPlannerAI.applyProposal")
        )}
      </button>
    )}
  </div>
</div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ModalPlannerAI;
